import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import {
  calcRevenueByMonth,
  calcTotalRevenueYtd,
  calcTotalRevenueYtdThrough,
  type YtdComparison,
} from "@/lib/kpi-calculations";

export interface TotalRevenueYtdData extends YtdComparison {
  reconciled: YtdComparison | null;
  /** Omsetning per måned i år og i fjor, indeks 0 = januar. */
  months: { current: number[]; previousYear: number[] };
}

/**
 * Total omsetning hittil i år (ekskl. MVA) med sammenligning vs samme periode
 * i fjor. Hero-KPI på admin-dashboardet.
 *
 * Erik-lock 2026-05-15. Bruker invoices lokalt cachet via
 * sync-poweroffice-invoices (PO Go er sannhetskilde).
 *
 * Status-filter og periode-logikk er i kpi-calculations.ts.
 */
export function useTotalRevenueYtd(reconciledThroughIso?: string) {
  return useQuery({
    queryKey: ["total-revenue-ytd", reconciledThroughIso ?? "live"],
    queryFn: async (): Promise<TotalRevenueYtdData> => {
      // Hent fakturaer fra i fjor og i år. Statusfilteret skjer i pure func.
      const previousYearStart = `${new Date().getFullYear() - 1}-01-01`;
      // Paginert: to år med fakturaer vokser mot 1000-rad-taket. VIB-1249.
      const data = await fetchAllPaginated<{
        invoice_date: string | null;
        status: string | null;
        amount_excl_vat: number | null;
      }>("invoices", "invoice_date, status, amount_excl_vat", (q) =>
        q.gte("invoice_date", previousYearStart),
      );

      const now = new Date();
      return {
        ...calcTotalRevenueYtd(data, now),
        months: {
          current: calcRevenueByMonth(data, now.getUTCFullYear()),
          previousYear: calcRevenueByMonth(data, now.getUTCFullYear() - 1),
        },
        reconciled: reconciledThroughIso
          ? calcTotalRevenueYtdThrough(data, now, reconciledThroughIso)
          : null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
